
import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch } from '@hooks/reduxHooks'
import { useSelector } from '@state/store'
import moment from 'moment'
import { GeneratorElutionDto, QualityControlStatus } from '@services/dtos'
import { getGeneratorElutionCurrentActivities, getGeneratorElutions, setGeneratorElutionFilters } from '@state/reducers/generatorElutionReducer'
import { GeneratorElutionDetailsDialog } from '../dialogs/GeneratorElutionDetailsDialog'
import InfoIcon from '@mui/icons-material/Info'
import { Chip, Container, Dialog, DialogContent, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import { ChipQualityControlStatus } from '@components/ChipQualityControlStatus'
import { QualityControlsDialog } from '@components/dialogs/QualityControlsDialog'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useGeneratorElutionSSE } from '@hooks/useGeneratorElutionSSE'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import FlakyIcon from '@mui/icons-material/Flaky'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { UpTransition } from '@components/animations'
import WasteEntryFromSourceForm from '@components/forms/WasteEntryFromSourceForm'
import { ChipGeneratorElutionStatus } from '../ChipGeneratorElutionStatus'

type ActionMenuProps = {
  row: GeneratorElutionDto
  onDetails: () => void
  onQualityControls: () => void
  onWaste: () => void
}

const ActionMenu = ({ row, onDetails, onQualityControls, onWaste }: ActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const close = () => setAnchorEl(null)

  return (
    <>
      <Tooltip title="Voir les détails">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDetails() }}>
          <InfoIcon fontSize="small" color="primary" />
        </IconButton>
      </Tooltip>
      {(row.qualityControlStatus !== QualityControlStatus.NONE || !row.wasteEntry) && (
        <>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget) }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close} onClick={(e) => e.stopPropagation()}>
            {row.qualityControlStatus !== QualityControlStatus.NONE && (
              <MenuItem onClick={() => { close(); onQualityControls() }}>
                <ListItemIcon><FlakyIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'primary' }}>Gérer les contrôles qualité</ListItemText>
              </MenuItem>
            )}
            {!row.wasteEntry && (
              <MenuItem onClick={() => { close(); onWaste() }}>
                <ListItemIcon><DeleteOutlineIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'primary' }}>Mise en déchet</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </>
  )
}

interface Props {
  containerClassName?: string
  className?: string
  onElutionHover?: (id: string | null) => void
}

export default function GeneratorElutionsTable({ containerClassName, className, onElutionHover }: Props) {
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const { generatorElutions, totalCount, loading } = useSelector(({ generatorElution }) => ({
    generatorElutions: generatorElution.items.datas,
    totalCount: generatorElution.items.totalCount,
    loading: generatorElution.loading,
  }))
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [qcDialogOpen, setQcDialogOpen] = useState(false)
  const [wasteDialogOpen, setWasteDialogOpen] = useState(false)
  const selectedElution = generatorElutions.find(e => e.id === selectedItemId) ?? null
  useGeneratorElutionSSE()
  const dispatch = useAppDispatch()
  const generatorElutionsRef = useRef(generatorElutions)

  useEffect(() => {
    generatorElutionsRef.current = generatorElutions
  }, [generatorElutions])

  useEffect(() => {
    dispatch(getGeneratorElutions())
    const interval = setInterval(() => {
      const ids = generatorElutionsRef.current.map(({ id }) => id)
      if (ids.length > 0) {
        dispatch(getGeneratorElutionCurrentActivities(ids))
      }
    }, 30_000)
    return () => {
      clearInterval(interval)
      dispatch(setGeneratorElutionFilters({ page: 0 }))
    }
  }, [])

  const columns: GridColumns<GeneratorElutionDto> = useMemo(
    () => [
      {
        field: '__actions__',
        headerName: 'Actions',
        width: 75,
        sortable: false,
        renderCell: (params: any) => {
          const row = params.row as GeneratorElutionDto
          return (
            <ActionMenu
              row={row}
              onDetails={() => { setSelectedItemId(row.id); setDetailsDialogOpen(true) }}
              onQualityControls={() => { setSelectedItemId(row.id); setQcDialogOpen(true) }}
              onWaste={() => { setSelectedItemId(row.id); setWasteDialogOpen(true) }}
            />
          )
        },
      },
      {
        field: 'status',
        headerName: 'Statut',
        width: 130,
        renderCell: (params) => <ChipGeneratorElutionStatus elution={params.row} />,
      },
      { field: 'no', headerName: 'N°', width: 50 },
      { field: 'activityRefAt', headerName: 'Date', width: 145,
        valueGetter: (params) => moment(params.row.activityRefAt).format('DD/MM/YYYY HH:mm') },
      { field: 'volumeMl', headerName: 'Volume (mL)', width: 105,
        renderCell: (params) => {
          const row = params.row as GeneratorElutionDto
          return (
            <div className="flex items-center gap-1">
              <span className="font-medium">{row.remainingVolumeMl.toFixed(2)}</span>
              <span>/ {row.volumeMl.toFixed(2)}</span>
            </div>
          )}
      },
      { field: 'activityAtRefMbq', headerName: 'Activité (MBq)', width: 155,   renderHeader: () => (
        <div className="flex items-center gap-1">
          <Tooltip title="Recalculée toutes les 30s par décroissance radioactive" arrow>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'pointer' }} />
          </Tooltip>
          <span className="font-medium text-sm">Activité (MBq)</span>
        </div>
        ),
        renderCell: (params) => {
          const row = params.row as GeneratorElutionDto
          if (row.activityAtTimeMbq === null) {
            return <span>{row.activityAtRefMbq.toFixed(2)}</span>
          }
          return (
            <div className="flex items-center gap-1">
              <span className="font-medium">{row.activityAtTimeMbq.toFixed(2)}</span>
              <span>/ {row.activityAtRefMbq.toFixed(2)}</span>
            </div>
          )
      }},
      {
        field: 'qualityControlStatus',
        headerName: 'CQ',
        width: 40,
        sortable: false,
        renderCell: (params) => <ChipQualityControlStatus status={params.row.qualityControlStatus} qualityControlDone={params.row.qualityControlDone} qualityControlTotal={params.row.qualityControlTotal} />,
      },
      { field: 'generatorDaughterIsotope', headerName: 'Isotope', width: 90,
        valueGetter: (params) => {
          const instances = params.row.generatorElutionInstances ?? []
          if (instances.length === 0) {
            return ''
          }
          return instances[0].generatorInstance.productBatch.productCatalog.generatorDaughterIsotope?.code 
        }
      },
    ],
    [],
  )

  return (
    <Container maxWidth={false} style={{ flex: 1, minHeight: 0, padding: '0px 30px', display: 'flex', flexDirection: 'column' }} className={containerClassName}>
      <DataGridPro
        density="standard"
        pagination
        pageSize={20}
        rowsPerPageOptions={[20]}
        paginationMode="server"
        style={{
          height: '100%',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '15px 15px 25px 25px',
        }}
        className={className}
        onPageChange={(page) => {
          dispatch(setGeneratorElutionFilters({ page }))
        }}
        sx={{
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f8f8f8',
          },
        }}
        columns={columns}
        rows={generatorElutions}
        rowCount={totalCount}
        loading={loading}
        disableSelectionOnClick
        disableMultipleSelection
        componentsProps={{
          row: {
            onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => {
              onElutionHover?.(e.currentTarget.dataset.id ?? null)
            },
            onMouseLeave: () => onElutionHover?.(null),
          },
        }}
      />

      <Dialog open={wasteDialogOpen} onClose={() => setWasteDialogOpen(false)} TransitionComponent={UpTransition} maxWidth="sm" fullWidth>
        <DialogContent>
          {wasteDialogOpen && selectedElution && (
            <WasteEntryFromSourceForm
              elution={selectedElution}
              onClose={() => { setWasteDialogOpen(false); setSelectedItemId(null) }}
            />
          )}
        </DialogContent>
      </Dialog>

      <GeneratorElutionDetailsDialog
        open={detailsDialogOpen}
        generatorElution={selectedElution}
        onClose={() => {
          setDetailsDialogOpen(false)
          setSelectedItemId(null)
        }}
      />

      {selectedElution && (
        <QualityControlsDialog
          open={qcDialogOpen}
          onClose={() => { setQcDialogOpen(false); setSelectedItemId(null) }}
          entityType="generatorElution"
          entity={selectedElution}
        />
      )}
    </Container>
  )
}
